// src/components/setup/ClassSetup.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { X, Calendar, Clock, Users, Video, Monitor } from 'lucide-react';
import { useClassroom } from '../../contexts/ClassroomContext';

const SetupContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const SetupModal = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const ClassModeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
`;

const ClassModeCard = styled.div`
  border: 2px solid ${props => props.selected ? '#4CAF50' : '#ddd'};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    border-color: #4CAF50;
  }
`;

const ModeIcon = styled.div`
  width: 60px;
  height: 40px;
  background: #333;
  border-radius: 4px;
  margin: 0 auto 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModeTitle = styled.h4`
  margin: 0 0 5px 0;
  color: #333;
`;

const ModeDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: #666;
`;

const TimeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 10px;
  align-items: end;
`;

const ButtonContainer = styled.div`
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &.primary {
    background: #4CAF50;
    color: white;
    
    &:hover {
      background: #45a049;
    }
  }
  
  &.secondary {
    background: #f0f0f0;
    color: #333;
    
    &:hover {
      background: #e0e0e0;
    }
  }
`;

const ClassSetup = () => {
  const navigate = useNavigate();
  const { createClassroom } = useClassroom();
  const [formData, setFormData] = useState({
    lessonName: 'Tutorial - 1',
    startDate: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5),
    duration: { hours: '00', minutes: '30' },
    classMode: 'online',
    seatNumber: '1V2',
    teacher: 'ClassIn',
    assistant: '',
    recordClassroom: false,
    recordScene: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDurationChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      duration: {
        ...prev.duration,
        [type]: value
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      const classroomData = {
        name: formData.lessonName,
        start_date: formData.startDate,
        start_time: formData.startTime,
        duration: `${formData.duration.hours}:${formData.duration.minutes}`,
        class_mode: formData.classMode,
        seat_number: formData.seatNumber,
        teacher: formData.teacher,
        assistant: formData.assistant,
        settings: {
          record_classroom: formData.recordClassroom,
          record_scene: formData.recordScene
        }
      };

      const classroom = await createClassroom(classroomData);
      if (classroom) {
        navigate(`/classroom/${classroom.id}`);
      }
    } catch (error) {
      console.error('Error creating classroom:', error);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <SetupContainer>
      <SetupModal>
        <ModalHeader>
          <ModalTitle>Add Lesson</ModalTitle>
          <CloseButton onClick={handleCancel}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormGroup>
            <Label>Lesson Name</Label>
            <Input
              value={formData.lessonName}
              onChange={(e) => handleInputChange('lessonName', e.target.value)}
              placeholder="Enter lesson name"
            />
          </FormGroup>

          <FormGroup>
            <Label>Start Date</Label>
            <Input
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Duration</Label>
            <TimeContainer>
              <Select
                value={formData.duration.hours}
                onChange={(e) => handleDurationChange('hours', e.target.value)}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i.toString().padStart(2, '0')}>
                    {i.toString().padStart(2, '0')} hours
                  </option>
                ))}
              </Select>
              <Select
                value={formData.duration.minutes}
                onChange={(e) => handleDurationChange('minutes', e.target.value)}
              >
                {['00', '15', '30', '45'].map(min => (
                  <option key={min} value={min}>{min} min</option>
                ))}
              </Select>
              <div style={{ padding: '12px 0', color: '#666' }}>
                Current Time<br />15:50
              </div>
            </TimeContainer>
          </FormGroup>

          <FormGroup>
            <Label>Class Mode</Label>
            <ClassModeContainer>
              <ClassModeCard
                selected={formData.classMode === 'online'}
                onClick={() => handleInputChange('classMode', 'online')}
              >
                <ModeIcon>
                  <Video color="white" size={24} />
                </ModeIcon>
                <ModeTitle>Online Classroom</ModeTitle>
                <ModeDescription>Suitable for online interactive teaching</ModeDescription>
              </ClassModeCard>
              <ClassModeCard
                selected={formData.classMode === 'smart'}
                onClick={() => handleInputChange('classMode', 'smart')}
              >
                <ModeIcon>
                  <Monitor color="white" size={24} />
                </ModeIcon>
                <ModeTitle>Smart Classroom</ModeTitle>
                <ModeDescription>Suitable for online merge offline interactive teaching with touch screen TV</ModeDescription>
              </ClassModeCard>
            </ClassModeContainer>
          </FormGroup>

          <FormGroup>
            <Label>Seat Number</Label>
            <Select
              value={formData.seatNumber}
              onChange={(e) => handleInputChange('seatNumber', e.target.value)}
            >
              <option value="1V2">1V2</option>
              <option value="1V4">1V4</option>
              <option value="1V6">1V6</option>
            </Select>
            <small style={{ color: '#666', fontSize: '12px' }}>
              1 teacher and 2 students on stage
            </small>
          </FormGroup>

          <FormGroup>
            <Label>Teacher</Label>
            <Input
              value={formData.teacher}
              onChange={(e) => handleInputChange('teacher', e.target.value)}
              readOnly
              style={{ background: '#f9f9f9' }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Assistant</Label>
            <Button 
              className="secondary"
              onClick={() => {/* Handle add assistant */}}
              style={{ width: '100%', textAlign: 'left' }}
            >
              Add
            </Button>
          </FormGroup>
        </ModalBody>

        <ButtonContainer>
          <Button className="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button className="primary" onClick={handleSubmit}>
            Create
          </Button>
        </ButtonContainer>
      </SetupModal>
    </SetupContainer>
  );
};

export default ClassSetup;